/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Text } from '@radix-ui/themes'
// removed router hooks since close action is handled by onClose
import {
  IconChevronDown,
  IconChevronUp,
  IconClock,
  IconX,
} from '@tabler/icons-react'
import { attemptService } from '@/services/attemptService'
import type { Attempt, AttemptResultDetail } from '@/types/attempt'
// import { useAuth } from '@/stores/authStore'
import { formatSecondsToHHMMSS, truncate } from '@/lib/utils'
import { hoverPressable } from '@/utils/style'
import { useGetPracticeTest } from '@/hooks/usePracticeTest'
import { Button } from '@/components/ui/button'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerOverlay,
} from '@/components/ui/drawer'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu'
import Hstack from '@/components/ui/hstack'
import Stack from '@/components/ui/stack'
import MarkingStatus from './marking-status-box'
import {
  Result,
  type ResultSection,
  type ResultItem,
  type ObjectiveOption,
} from './result'
import { FormatFullDateMonthYearHourMinute } from '@/utils/time'

interface AttemptHeader {
  class_id: number
  class_name: string
  practice_test_name: string
  module_name: string
  student_name: string
}

interface ResultDrawerProps {
  isOpen: boolean
  onClose: () => void
  practiceTestId?: string
  studentId?: number
}

export default function MockTestResult({
  isOpen,
  onClose,
  practiceTestId: practiceTestIdProp,
  studentId,
}: ResultDrawerProps) {
  const practiceTestId = practiceTestIdProp
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [_attempts, setAttempts] = useState<Attempt[]>([])
  const [detail, setDetail] = useState<AttemptResultDetail | null>(null)
  const [selectedAttemptId, setSelectedAttemptId] = useState<number | null>(
    null
  )
  const [isAttemptOpen, setIsAttemptOpen] = useState(false)
  const [sectionsData, setSectionsData] = useState<ResultSection[]>([])
  const [attemptData, setAttemptData] = useState<AttemptHeader | null>(null)

  const hasFetchedRef = useRef(false)
  useEffect(() => {
    async function fetchLatest() {
      if (!isOpen || !practiceTestId || !studentId) return
      if (hasFetchedRef.current) return
      hasFetchedRef.current = true
      setIsLoading(true)
      setErrorMessage(null)
      try {
        const listRes = await attemptService.getAttemptList({
          student_id: Number(studentId),
          practice_test_id: Number(practiceTestId),
        })

        const attempts = listRes.attempts || []
        setAttemptData({
          class_id: listRes.class_id,
          class_name: listRes.class_name,
          practice_test_name: listRes.practice_test_name,
          module_name: listRes.module_name,
          student_name: listRes.student_name,
        })
        setAttempts(attempts)

        if (attempts.length === 0) {
          setErrorMessage('No attempts found')
          return
        }

        const latestAttempt = attempts.reduce((latest, current) => {
          const latestTime = new Date(
            latest.end_time || latest.last_activity || latest.start_time
          ).getTime()
          const currentTime = new Date(
            current.end_time || current.last_activity || current.start_time
          ).getTime()
          return currentTime > latestTime ? current : latest
        })

        const resultRes = await attemptService.getAttemptResult(
          latestAttempt.id
        )
        setDetail(resultRes)
      } catch (_error) {
        setErrorMessage('Failed to load attempt data')
      } finally {
        setIsLoading(false)
      }
    }
    fetchLatest()
  }, [isOpen, studentId, practiceTestId])

  useEffect(() => {
    if (!isOpen) hasFetchedRef.current = false
  }, [isOpen])

  useEffect(() => {
    if (detail?.attempt_id) setSelectedAttemptId(detail.attempt_id)
  }, [detail?.attempt_id])

  const attemptOptions = (() => {
    if (!_attempts.length) return [] as { id: number; label: string }[]
    // const sorted = [..._attempts].sort(
    //   (a, b) =>
    //     new Date(b.end_time || b.last_activity || b.start_time).getTime() -
    //     new Date(a.end_time || a.last_activity || a.start_time).getTime()
    // );
    return _attempts.map((a, idx) => ({
      id: a.id,
      label: `Attempt: ${idx + 1}`,
    }))
  })()

  const attemptLabel = (() => {
    if (!attemptOptions.length) return 'Attempt: 1'
    const found = attemptOptions.find((o) => o.id === selectedAttemptId)
    return found?.label ?? attemptOptions[0]?.label ?? 'Attempt'
  })()

  const handleAttemptChange = async (attemptId: number) => {
    if (attemptId === selectedAttemptId) return

    setSelectedAttemptId(attemptId)
    setIsLoading(true)
    setErrorMessage(null)

    try {
      const resultRes = await attemptService.getAttemptResult(attemptId)
      setDetail(resultRes)
    } catch (_error) {
      setErrorMessage('Failed to load attempt result')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDataRefresh = async () => {
    if (!selectedAttemptId) return

    try {
      const resultRes = await attemptService.getAttemptResult(selectedAttemptId)
      setDetail(resultRes)
    } catch (_error) {
      setErrorMessage('Failed to refresh attempt result')
    }
  }

  const { data: _practiceTestData } = useGetPracticeTest({
    practice_test_id: practiceTestId || '',
  })

  useEffect(() => {
    function getQuestionType(type: unknown) {
      const t = String(type || '').toLowerCase()
      if (t.includes('single_choice')) return 'single_choice'
      if (t.includes('multiple_choice')) return 'multiple_choice'
      if (t.includes('choice')) return 'choice'
      return 'text'
    }

    if (!detail?.sections) {
      setSectionsData([])
      return
    }

    let globalCounter = 1
    const mapped: ResultSection[] = detail.sections.map((section: any) => {
      const items: ResultItem[] = []

      // Normal (non-case-study) questions
      for (const question of section.questions || []) {
        // Check if this question has multiple sub-questions
        const hasMultipleSubQuestions =
          question.sub_questions && question.sub_questions.length > 1

        if (hasMultipleSubQuestions) {
          // Build a parent question card with children as sub-questions
          const startCounter = globalCounter

          const children: ResultItem[] = []
          for (const subQuestion of question.sub_questions || []) {
            // Compute letter based on current sub-question index within the same question
            const subLetter = String.fromCharCode(97 + children.length)
            const questionType = getQuestionType(subQuestion.type)
            const isObj =
              questionType === 'single_choice' ||
              questionType === 'multiple_choice' ||
              questionType === 'choice'
            const options: ObjectiveOption[] = isObj
              ? (subQuestion.options || []).map((o: any) => {
                  const id =
                    o?.option_id != null
                      ? String(o.option_id)
                      : String(o?.id || '')
                  const label = o?.text || String(o || '')
                  return { id, label }
                })
              : []

            // Get correct answers from options
            const correctAnswers = isObj
              ? options
                  .filter((opt) => {
                    // Find the corresponding option in subQuestion.options
                    const originalOption = subQuestion.options?.find(
                      (o: any) => String(o.option_id || o.id) === opt.id
                    )
                    return originalOption?.is_correct === true
                  })
                  .map((opt) => opt.id)
              : []

            children.push({
              id: `${question.question_id}-${subQuestion.sub_question_id}`,
              subQuestionId: subQuestion.sub_question_id,
              answerSheetUrl:
                subQuestion.sub_question_answer_sheet_link || undefined,
              title: `${startCounter}${subLetter}.`,
              prompt: subQuestion.text || '',
              type: isObj ? 'objective' : 'subjective',
              questionType:
                questionType === 'choice' ? undefined : questionType,
              answerHtml: !isObj
                ? subQuestion.sub_question_answer_text || ''
                : undefined,
              objectiveOptions: isObj ? options : undefined,
              objectiveAnswer: isObj
                ? (subQuestion.sub_question_option_answers || []).map(String)
                : undefined,
              objectiveCorrect: isObj ? correctAnswers : undefined,
              variant: 'normal',
              marks: subQuestion.marks,
              marking: subQuestion.markings,
              sub_question_answer_sheet_link_data:
                subQuestion.sub_question_answer_sheet_link_data || [],
            })
          }
          globalCounter++

          // Add parent card containing sub-question cards
          items.push({
            id: `parent-${question.question_id}`,
            title: `Question ${startCounter}`,
            prompt: question.question_text || '',
            type: 'subjective',
            variant: 'multiSubHeader',
            children,
          })
        } else {
          // Single sub-question - display normally
          for (const subQuestion of question.sub_questions || []) {
            const questionType = getQuestionType(subQuestion.type)
            const isObj =
              questionType === 'single_choice' ||
              questionType === 'multiple_choice' ||
              questionType === 'choice'
            const options: ObjectiveOption[] = isObj
              ? (subQuestion.options || []).map((o: any) => {
                  const id =
                    o?.option_id != null
                      ? String(o.option_id)
                      : String(o?.id || '')
                  const label = o?.text || String(o || '')
                  return { id, label }
                })
              : []

            // Get correct answers from options
            const correctAnswers = isObj
              ? options
                  .filter((opt) => {
                    // Find the corresponding option in subQuestion.options
                    const originalOption = subQuestion.options?.find(
                      (o: any) => String(o.option_id || o.id) === opt.id
                    )
                    return originalOption?.is_correct === true
                  })
                  .map((opt) => opt.id)
              : []

            items.push({
              id: `${question.question_id}-${subQuestion.sub_question_id}`,
              title: `Question ${globalCounter++}`,
              subQuestionId: subQuestion.sub_question_id,
              answerSheetUrl:
                subQuestion.sub_question_answer_sheet_link || undefined,
              prompt: subQuestion.text || question.question_text || '',
              type: isObj ? 'objective' : 'subjective',
              questionType:
                questionType === 'choice' ? undefined : questionType,
              answerHtml: !isObj
                ? subQuestion.sub_question_answer_text || ''
                : undefined,
              objectiveOptions: isObj ? options : undefined,
              objectiveAnswer: isObj
                ? (subQuestion.sub_question_option_answers || []).map(String)
                : undefined,
              objectiveCorrect: isObj ? correctAnswers : undefined,
              variant: 'normal',
              marks: subQuestion.marks,
              marking: subQuestion.markings,
              sub_question_answer_sheet_link_data:
                subQuestion.sub_question_answer_sheet_link_data || [],
            })
          }
        }
      }

      // Grouped case studies
      if (section.case_studies && section.case_studies.length) {
        for (const cs of section.case_studies) {
          // Capture the starting question number for this case study
          const caseStudyStartCounter = globalCounter
          const children: ResultItem[] = []
          for (const q of cs.questions || []) {
            // Check if this question has multiple sub-questions
            const hasMultipleSubQuestions =
              q.sub_questions && q.sub_questions.length > 1

            if (hasMultipleSubQuestions) {
              // Build a nested parent under case study with children as sub-questions
              const startCounter = globalCounter

              const subChildren: ResultItem[] = []
              for (const sub of q.sub_questions || []) {
                const subLetter = String.fromCharCode(97 + subChildren.length)
                const questionType = getQuestionType(sub.type)
                const isObj =
                  questionType === 'single_choice' ||
                  questionType === 'multiple_choice' ||
                  questionType === 'choice'
                const options: ObjectiveOption[] = isObj
                  ? (sub.options || []).map((o: any) => {
                      const id =
                        o?.option_id != null
                          ? String(o.option_id)
                          : String(o?.id || '')
                      const label = o?.text || String(o || '')
                      return { id, label }
                    })
                  : []
                // Get correct answers from options for case study
                const correctAnswers = isObj
                  ? options
                      .filter((opt) => {
                        // Find the corresponding option in sub.options
                        const originalOption = sub.options?.find(
                          (o: any) => String(o.option_id || o.id) === opt.id
                        )
                        return originalOption?.is_correct === true
                      })
                      .map((opt) => opt.id)
                  : []

                subChildren.push({
                  id: `cs-${cs.case_study_id}-${q.question_id}-${sub.sub_question_id}`,
                  title: `${startCounter}${subLetter}.`,
                  subQuestionId: sub.sub_question_id,
                  answerSheetUrl:
                    sub.sub_question_answer_sheet_link || undefined,
                  prompt: sub.text || '',
                  type: isObj ? 'objective' : 'subjective',
                  questionType:
                    questionType === 'choice' ? undefined : questionType,
                  answerHtml: !isObj
                    ? sub.sub_question_answer_text || ''
                    : undefined,
                  objectiveOptions: isObj ? options : undefined,
                  objectiveAnswer: isObj
                    ? (sub.sub_question_option_answers || []).map(String)
                    : undefined,
                  objectiveCorrect: isObj ? correctAnswers : undefined,
                  variant: 'normal',
                  marks: sub.marks,
                  marking: sub.markings,
                  isSingleSubInCaseStudy: false,
                  sub_question_answer_sheet_link_data:
                    sub.sub_question_answer_sheet_link_data || [],
                })
              }
              globalCounter++

              // Add a parent card under the case study containing its sub-questions
              children.push({
                id: `cs-parent-${cs.case_study_id}-${q.question_id}`,
                title: `Question ${startCounter}`,
                prompt: q.question_text || '',
                type: 'subjective',
                variant: 'caseStudy',
                children: subChildren,
              })
            } else {
              // Single sub-question - display normally
              for (const sub of q.sub_questions || []) {
                const questionType = getQuestionType(sub.type)
                const isObj =
                  questionType === 'single_choice' ||
                  questionType === 'multiple_choice' ||
                  questionType === 'choice'
                const options: ObjectiveOption[] = isObj
                  ? (sub.options || []).map((o: any) => {
                      const id =
                        o?.option_id != null
                          ? String(o.option_id)
                          : String(o?.id || '')
                      const label = o?.text || String(o || '')
                      return { id, label }
                    })
                  : []
                // Get correct answers from options for case study
                const correctAnswers = isObj
                  ? options
                      .filter((opt) => {
                        // Find the corresponding option in sub.options
                        const originalOption = sub.options?.find(
                          (o: any) => String(o.option_id || o.id) === opt.id
                        )
                        return originalOption?.is_correct === true
                      })
                      .map((opt) => opt.id)
                  : []

                children.push({
                  id: `cs-${cs.case_study_id}-${q.question_id}-${sub.sub_question_id}`,
                  subQuestionId: sub.sub_question_id,
                  answerSheetUrl:
                    sub.sub_question_answer_sheet_link || undefined,
                  title: `Question ${globalCounter++}`,
                  prompt: sub.text || q.question_text || '',
                  type: isObj ? 'objective' : 'subjective',
                  questionType:
                    questionType === 'choice' ? undefined : questionType,
                  answerHtml: !isObj
                    ? sub.sub_question_answer_text || ''
                    : undefined,
                  objectiveOptions: isObj ? options : undefined,
                  objectiveAnswer: isObj
                    ? (sub.sub_question_option_answers || []).map(String)
                    : undefined,
                  objectiveCorrect: isObj ? correctAnswers : undefined,
                  variant: 'normal',
                  marks: sub.marks,
                  marking: sub.markings,
                  isSingleSubInCaseStudy: true,
                  sub_question_answer_sheet_link_data:
                    sub.sub_question_answer_sheet_link_data || [],
                })
              }
            }
          }
          items.push({
            id: `cs-${cs.case_study_id}`,
            title: `Question ${caseStudyStartCounter}-${globalCounter - 1}`,
            prompt: cs.case_study || '',
            type: 'subjective',
            variant: 'caseStudy',
            children,
          })
        }
      }

      return {
        id: section.section,
        title: `Section ${section.section}`,
        questions: items,
      }
    })

    setSectionsData(mapped)
  }, [detail])

  const timeTaken = useMemo<number>(() => {
    if (!detail) return 0
    const start = new Date(detail.start_time).getTime()
    const lastTime = new Date(detail.last_activity).getTime()
    const diffMs = lastTime - start
    if (Number.isNaN(diffMs) || diffMs <= 0) return 0
    return Math.round(diffMs / 1000)
  }, [detail])

  const data = {
    aiMarks: detail?.exam_marks?.ai_mark,
    ...(detail?.ai_marked === true && {
      aiStatus: detail?.exam_marks?.ai_result,
    }),
    tutorMarks: detail?.exam_marks?.tutor_mark,
    ...(detail?.tutor_marked !== 'unmarked' && {
      tutorStatus: detail?.exam_marks?.tutor_result,
    }),
    totalMarks: detail?.exam_marks?.total_mark,
  }

  return (
    <Drawer
      direction='bottom'
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      dismissible
      autoFocus
      repositionInputs
      modal={true}
    >
      <DrawerOverlay className='bg-[#000000B2]' />
      <DrawerContent className='flex h-full flex-col bg-[#F6F7F9] p-0'>
        <DrawerHeader className='sticky top-0 z-20 flex-shrink-0 border-b border-[#EAECF0] bg-[#F6F7F9] p-0'>
          <Box className='mx-auto w-full px-6 py-4'>
            <div className='flex w-full items-center justify-between gap-4'>
              <Stack gap={'0px'}>
                <Hstack gap='24px'>
                  <Text className='max-w-[800px] text-left text-[20px] font-bold text-wrap text-[#002652] capitalize'>
                    {attemptData?.class_name && (
                      <>{attemptData?.class_name} - </>
                    )}
                    {truncate(attemptData?.practice_test_name, 50) || 'N/A'} -{' '}
                    {attemptData?.student_name || 'N/A'}
                  </Text>

                  {attemptOptions && attemptOptions.length > 0 && (
                    <Box className='flex items-center gap-2'>
                      <DropdownMenu
                        open={isAttemptOpen}
                        onOpenChange={setIsAttemptOpen}
                      >
                        <DropdownMenuTrigger asChild>
                          <button
                            type='button'
                            className='rounded-md bg-white px-3 py-2 text-sm font-medium text-[#0B2B57] shadow-sm focus:outline-hidden'
                            aria-label='Attempt selector'
                          >
                            <Hstack gapX='4px'>
                              <span className='flex items-center text-sm font-medium text-[#0B2B57]'>
                                {attemptLabel}
                              </span>
                              {isAttemptOpen ? (
                                <IconChevronUp />
                              ) : (
                                <IconChevronDown />
                              )}
                            </Hstack>
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align='start'>
                          {attemptOptions.length > 0 &&
                            attemptOptions.map((opt) => (
                              <DropdownMenuItem
                                key={opt.id}
                                onClick={() => handleAttemptChange(opt.id)}
                                aria-label={`Select ${opt.label}`}
                                className='cursor-pointer'
                              >
                                {opt.label}
                              </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </Box>
                  )}
                </Hstack>
                <Text className='truncate text-left text-[14px] text-[#002652]/90'>
                  {detail?.last_activity
                    ? FormatFullDateMonthYearHourMinute(detail.last_activity)
                    : 'N/A'}
                </Text>
              </Stack>

              <Hstack gap={'12px'}>
                <Hstack
                  gap={'8px'}
                  className='h-[40px] rounded-[12px] bg-[#D9D9D9] px-[12px] py-[8px]'
                  align={'center'}
                >
                  <IconClock size={20} color='#002652' aria-hidden />
                  <Text className='w-fit text-[14px] text-[#002652]'>
                    Time taken:
                  </Text>
                  <Text className='inline-block min-w-[68px] truncate text-center text-[18px] text-[#002652]'>
                    {formatSecondsToHHMMSS(timeTaken)}
                  </Text>
                </Hstack>
                <Button
                  onClick={onClose}
                  className={`h-[40px] w-[40px] rounded-md bg-[#F6F7F9] p-0 text-sm font-medium text-[#2F62D6] hover:bg-gray-50 ${hoverPressable}`}
                  aria-label='Close drawer'
                >
                  <IconX size={20} color='#2F62D6' />
                </Button>
              </Hstack>
            </div>
          </Box>
          <DrawerTitle className='hidden' />
          <DrawerDescription className='hidden' />
        </DrawerHeader>

        {/* Scrollable body */}
        <div className='flex-1 overflow-y-auto'>
          <Box className='mx-auto w-full px-6 py-4'>
            <MarkingStatus {...data} />
            {_practiceTestData?.general_information !== null ||
              (_practiceTestData?.general_information !== '<p></p>' && (
                <div className='z-10 rounded-xl border border-[#EAECF0] bg-white px-6 py-4'>
                  <Text className='text-[20px] font-[400] text-[#002652]'>
                    General Information
                  </Text>
                  {_practiceTestData?.general_information && (
                    <div
                      className='prose max-w-none'
                      dangerouslySetInnerHTML={{
                        __html:
                          _practiceTestData?.general_information as string,
                      }}
                    />
                  )}
                </div>
              ))}
            <main className='z-20 my-4 flex w-full flex-col items-center rounded-lg bg-white px-6 py-4'>
              {isLoading && (
                <Text className='text-center text-xl'>
                  Loading latest attempt...
                </Text>
              )}

              {!isLoading && errorMessage && (
                <Text className='text-center text-xl text-red-700'>
                  {errorMessage}
                </Text>
              )}

              {!isLoading &&
                !errorMessage &&
                (!sectionsData || sectionsData.length === 0) && (
                  <Text className='text-center text-xl'>
                    No questions to display.
                  </Text>
                )}

              <Result
                sections={sectionsData || []}
                onDataRefresh={handleDataRefresh}
              />
            </main>
          </Box>
        </div>
      </DrawerContent>
    </Drawer>
  )
}
